import type { SearchableType, SearchResults } from '@whn/shared';
import { useQuery } from '@tanstack/react-query';
import { api, toQueryString } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';

/**
 * Search runs on the term the caller has already debounced.
 *
 * Debouncing here rather than in the component would put a timer inside a
 * hook that also owns a cache — two mechanisms for "don't ask again yet"
 * fighting each other. The palette debounces its input; this just caches.
 */
export function useSearch(term: string, type?: SearchableType) {
  const enabled = term.trim().length >= 2;
  const params = { q: term.trim(), type };

  return useQuery({
    queryKey: queryKeys.search(params),
    queryFn: ({ signal }) => api.get<SearchResults>(`/search${toQueryString(params)}`, signal),
    enabled,
    // Results for a term the user is still typing go stale immediately, but
    // keeping them briefly makes backspacing feel instant.
    staleTime: 15_000,
    placeholderData: (previous) => previous,
  });
}
